import React from 'react';
import { css } from '@emotion/react';
import { Icon } from '@gumtree/ui-library';
import { breakpoints, mediaQuery, fontWeights, gutterSizes, colors } from '@gumtree/ui-library/src/base/theme';

import fallbackImageM from '../../../../imgs/bing-fallback-m.png'; 
import fallbackImagePc from '../../../../imgs/bing-fallback-pc.png'; 

interface BingFallbackProps {
    /**
     * 是否用于 Google 广告占位
     * 如果为 true，移动端宽度为 100%（不减去 16px）
     */
    isGoogleAdPlaceholder?: boolean;
}

const styles = {
    container: (isGoogleAdPlaceholder?: boolean) => css`
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        text-decoration: none;
        cursor: pointer;
        overflow: hidden;
        border-radius: 8px;
        
        /* 兜底背景色 - 当背景图加载失败时显示 */
        background-color: #dbdadb;
        
        /* PC */
        background-image: url(${fallbackImagePc});
        background-repeat: no-repeat;
        background-position: center;
        background-size: cover;

        box-shadow: 0px 1px 6px 0px #00000033;

        /* m */
        ${mediaQuery.until(breakpoints.medium)} {
            background-image: url(${fallbackImageM});
            flex-direction: column;
            padding: ${gutterSizes.large} ${gutterSizes.large};
            width: ${isGoogleAdPlaceholder ? '100%' : 'calc(100% - 16px)'};
            margin: ${isGoogleAdPlaceholder ? '0' : '0 auto'};
        }

        /* PC */
        ${mediaQuery.from(breakpoints.medium)} {
            flex-direction: row;
            padding: ${gutterSizes.large} ${gutterSizes.xlarge};
            justify-content: flex-start;
        }
    `,
    gumtreeIcon: css`
        width: 30px;
        height: 38px;
        position: absolute;
        right: 24px;
        bottom: 24px;
        z-index: 2;
        color: ${colors.white};

        ${mediaQuery.until(breakpoints.medium)} {
            width: 16px;
            height: 19px;
            right: 16px;

            &:before {
                width: 16px !important;
                height: 19px !important;
                background-size: 16px 19px !important;
            }
        }
    `,
    content: css`
        width: 100%;
        position: relative;
        z-index: 1;
        display: flex;
        flex-direction: column;
        color: ${colors.white};

        align-items: flex-start;
        text-align: left;
    `,
    title: css`
        font-size: 24px;
        font-weight: ${fontWeights.bold};
        color: ${colors.white};
        margin-bottom: ${gutterSizes.base};

        font-family: Readex Pro;
        font-weight: 600;
        font-style: SemiBold;
        font-size: 20px;
        leading-trim: NONE;
        line-height: 28px;
        letter-spacing: 1%;

        ${mediaQuery.until(breakpoints.medium)} {
            font-size: 17px;
            line-height: 26px;
        }
    `,
    subtitle: css`
        font-family: Readex Pro;
        font-weight: 330;
        font-style: Regular;
        font-size: 13px;
        color: ${colors.white};
        leading-trim: NONE;
        line-height: 20px;
        letter-spacing: 1%;

        margin-bottom: 27px;

        ${mediaQuery.until(breakpoints.medium)} {
            font-size: 14px;
            margin-bottom: 29px;
        }
    `,
    button: css`
        width: 120px;
        min-width: 80px;
        angle: 0 deg;
        opacity: 1;
        gap: 12px;
        border-radius: 6px;
        background: #5CE00B;

        font-family: Readex Pro;
        font-weight: 600;
        font-style: SemiBold;
        font-size: 15px;
        leading-trim: NONE;
        line-height: 40px;
        letter-spacing: 0%;
        text-align: center;
        color:  #0D495C;
    `
};

export const BingFallback = ({ isGoogleAdPlaceholder = false }: BingFallbackProps = {}) => {
    return (
        <a 
            href="/postad/category?utm_source=bing3pa" 
            css={styles.container(isGoogleAdPlaceholder)}
            aria-label="One place for all your Ads. Post an Ad for nearly anything."
        >
            <Icon css={styles.gumtreeIcon} type="gumtree" />
            <div css={styles.content}>
                <div css={styles.title}>One place for all your Ads</div>
                <div css={styles.subtitle}>Post an Ad for nearly anything</div>
                <span css={styles.button}>Post an Ad</span>
            </div>
        </a>
    );
};
